import { useEffect, useState } from "react";
import { BarChart3, BookOpen, ClipboardList, GraduationCap, TrendingUp, Users } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";

const COLORS = ["#F97316", "#0EA5E9", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#14B8A6", "#F43F5E"];

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-500 text-sm py-20 text-center" data-testid="admin-dashboard-loading">Loading dashboard…</div>;

  const { metrics, charts } = data;
  const cards = [
    [Users, "Total Enquiries", metrics.total_enquiries],
    [TrendingUp, "New Leads", metrics.new_leads],
    [ClipboardList, "Applications", metrics.applications],
    [GraduationCap, "Active Students", metrics.active_students],
    [BookOpen, "Published Courses", metrics.courses],
    [BarChart3, "Certificates Issued", metrics.certificates],
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-2xl font-bold text-white mb-1">Dashboard</h1>
      <p className="text-xs text-slate-500 mb-8">Live counts from the database — includes clearly-marked demo records.</p>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-10" data-testid="admin-metrics">
        {cards.map(([Icon, label, value]) => (
          <div key={label} className="bg-card border border-white/10 rounded-2xl p-5">
            <Icon className="w-5 h-5 text-orange-400 mb-3" />
            <div className="font-display text-2xl font-extrabold text-white" data-testid={`metric-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>{value}</div>
            <div className="text-[11px] text-slate-500 mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <ChartCard title="Monthly Applications" testid="chart-monthly">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={charts.monthly_applications}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#132338", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
              <Line type="monotone" dataKey="applications" stroke="#F97316" strokeWidth={2.5} dot={{ fill: "#F97316" }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Enquiries by Course" testid="chart-leads-course">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.leads_by_course} layout="vertical">
              <CartesianGrid stroke="rgba(255,255,255,0.06)" />
              <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={150} tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#132338", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
              <Bar dataKey="count" fill="#F97316" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Leads by Source" testid="chart-leads-source">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={charts.leads_by_source} dataKey="count" nameKey="name" outerRadius={90} label={{ fill: "#94a3b8", fontSize: 11 }}>
                {charts.leads_by_source.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#132338", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Applications by Status" testid="chart-app-status">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.applications_by_status}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#132338", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
              <Bar dataKey="count" fill="#0EA5E9" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-card border border-white/10 rounded-2xl p-6" data-testid="recent-leads">
          <h3 className="font-display font-semibold text-white mb-4">Recent Enquiries</h3>
          <ul className="space-y-3">
            {data.recent_leads.map((l) => (
              <li key={l.id} className="flex justify-between gap-3 text-sm border-b border-white/5 pb-3">
                <div>
                  <span className="text-white font-medium">{l.name}</span>
                  {l.demo && <span className="text-[9px] text-slate-500 border border-white/15 rounded-full px-1.5 py-0.5 ml-2 uppercase">Demo</span>}
                  <div className="text-xs text-slate-500">{l.course_interest || l.type} • {l.source}</div>
                </div>
                <span className="text-xs text-orange-400 shrink-0">{l.status}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-card border border-white/10 rounded-2xl p-6" data-testid="recent-applications">
          <h3 className="font-display font-semibold text-white mb-4">Recent Applications</h3>
          <ul className="space-y-3">
            {data.recent_applications.length === 0 && <li className="text-sm text-slate-500">No applications yet.</li>}
            {data.recent_applications.map((a) => (
              <li key={a.id} className="flex justify-between gap-3 text-sm border-b border-white/5 pb-3">
                <div>
                  <span className="text-white font-medium">{a.full_name}</span>
                  <div className="text-xs text-slate-500 font-mono">{a.application_id} • {a.course_title}</div>
                </div>
                <span className="text-xs text-orange-400 shrink-0">{a.status}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div className="bg-card border border-white/10 rounded-2xl p-6" data-testid={testid}>
      <h3 className="font-display font-semibold text-white mb-4 text-sm">{title}</h3>
      {children}
    </div>
  );
}
